/**
 * WorkshopCalendarPage — Phase 2c KFZ-DMS.
 *
 * Werkstatt-Kalender mit Ressourcen-Zeilen (Hebebühnen, Mitarbeiter, Stationen)
 * und HTML5-Drag&Drop. Tag- oder Wochen-Ansicht; ungeplante Aufträge
 * werden links als verschiebbare Karten angezeigt.
 *
 * Aufruf: /dealer/workshop-calendar
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Settings, Plus, X,
    Trash2, Edit2, Wrench, AlertTriangle, Car, Inbox, Clock, Ban, Smartphone, Share2, Printer, Search, Gauge, MapPin,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { SettingsShortcut } from "../components/dealer/SettingsShortcut";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import InfoHint from "../components/listing/InfoHint";
import DateTimeField from "../components/common/DateTimeField";
import { Badge } from "../components/ui/badge";
import { orderCategories } from "../components/workshop/workshopConstants";
import PublicBookingLink from "../components/workshop/PublicBookingLink";
import ShareOrderModal from "../components/workshop/ShareOrderModal";
import UtilizationModal from "../components/workshop/UtilizationModal";
import { useGearsVisible } from "../lib/gearVisibility";

// Kategorie-Farbbalken (Abrechnungsart) für Auftrags-Blöcke im Kalender.
function completionAmpel(estimatedCompletion) {
    if (!estimatedCompletion) return null;
    const todayStr = tzTodayStr();
    const dStr = String(estimatedCompletion).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dStr)) return null;
    if (dStr < todayStr) return { c: "bg-red-500", t: "überfällig" };
    if (dStr === todayStr) return { c: "bg-amber-500", t: "heute fällig" };
    return { c: "bg-emerald-500", t: "im Plan" };
}
function fmtDateDe(s) {
    if (!s) return "";
    const d = new Date(String(s).slice(0, 10) + "T00:00:00");
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function CategoryStripe({ order }) {
    const cats = orderCategories(order);
    return (
        <div className="absolute left-0 top-0 bottom-0 w-1.5 flex flex-col overflow-hidden" aria-hidden="true" data-testid={`cal-catbar-${order.id}`}>
            {cats.map((c) => (
                <div key={c.key} className="flex-1" style={{ backgroundColor: c.hex }} title={c.label} />
            ))}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
//  Helpers
// ──────────────────────────────────────────────────────────────────────────
const SLOT_MIN = 30;           // 30-Minuten-Raster
const DEFAULT_HOUR_START = 8;
const DEFAULT_HOUR_END = 18;
const PX_PER_SLOT = 22;         // 30 min = 22px → kompakter Kalender (Aug-2026, User-Wunsch: ohne großes Scrollen)
const slotsCount = (hs, he) => (he - hs) * (60 / SLOT_MIN);

// Vorgegebene Spezialisierungen (synchron zum Backend models_workshop_calendar.py)
const SPECIALTIES = [
    "Aufbereitung/Auslieferung",
    "Verschleiss/Wartung",
    "Mechanik/Motor",
    "Diagnose/Annahme",
    "Elektrik/Elektronik",
];

// Kalender-Raster aus den Öffnungszeiten der Filiale ableiten (min Start / max Ende).
function deriveGridHours(structured, enabled) {
    if (!enabled || !Array.isArray(structured) || structured.length === 0) {
        return { start: DEFAULT_HOUR_START, end: DEFAULT_HOUR_END };
    }
    let min = 24, max = 0;
    structured.forEach((r) => {
        if (!r || r.closed) return;
        const o = parseInt(String(r.open || "").slice(0, 2), 10);
        const c = parseInt(String(r.close || "").slice(0, 2), 10);
        const cm = String(r.close || "").slice(3, 5);
        if (!Number.isNaN(o)) min = Math.min(min, o);
        if (!Number.isNaN(c)) max = Math.max(max, cm !== "00" ? c + 1 : c);
    });
    if (min >= max || max === 0) return { start: DEFAULT_HOUR_START, end: DEFAULT_HOUR_END };
    return { start: Math.floor(min), end: Math.ceil(max) };
}

const STATUS_COLOR = {
    draft: "bg-gray-200 border-gray-400 text-gray-800",
    quote: "bg-purple-100 border-purple-400 text-purple-900",
    confirmed: "bg-blue-100 border-blue-500 text-blue-900",
    in_progress: "bg-amber-100 border-amber-500 text-amber-900",
    completed: "bg-green-100 border-green-500 text-green-900",
};
const RESOURCE_TYPE_LABEL = {
    lift: "Hebebühne",
    mechanic: "Mitarbeiter",
    station: "Station",
    other: "Sonstiges",
};
const RESOURCE_TYPE_ICON = {
    lift: "🛠",
    mechanic: "👤",
    station: "⚙",
    other: "•",
};
// Mechaniker-Spezialitäten (Schlüssel ↔ deutsches Label), synchron zu workshop_orders_router._SPECIALTY_KEYWORDS
const SPEC_LABEL = {
    aufbereitung: "Aufbereitung",
    lehrling: "Lehrling",
    mechanik: "Mechanik",
    verschleiss: "Verschleiß",
};

// Auftrags-Status auf Deutsch (User-Wunsch Jun-2026)
const STATUS_LABEL_DE = {
    draft: "Entwurf",
    quote: "Angebot",
    confirmed: "Bestätigt",
    in_progress: "In Arbeit",
    completed: "Fertig",
    invoiced: "Abgerechnet",
    cancelled: "Storniert",
};

// ── Zeitzonen-Handling (Jul-2026, User-Wunsch) ────────────────────────────
// Termine werden IMMER in der gewählten Kalender-Zeitzone dargestellt
// (Standard Europe/Berlin) — unabhängig von der Browser-Zeitzone. Für den
// europaweiten Einsatz ist die Zone umschaltbar (Auswahl im Kalender-Header).
export const CAL_TZ_OPTIONS = [
    "Europe/Berlin", "Europe/Vienna", "Europe/Zurich", "Europe/Amsterdam",
    "Europe/Brussels", "Europe/Paris", "Europe/Rome", "Europe/Madrid",
    "Europe/Lisbon", "Europe/London", "Europe/Dublin", "Europe/Warsaw",
    "Europe/Prague", "Europe/Copenhagen", "Europe/Stockholm", "Europe/Oslo",
    "Europe/Helsinki", "Europe/Athens", "Europe/Bucharest", "Europe/Budapest",
];
let CAL_TZ = localStorage.getItem("calendarTz") || "Europe/Berlin";
export const getCalTz = () => CAL_TZ;
export const setCalTz = (tz) => { CAL_TZ = tz; localStorage.setItem("calendarTz", tz); };

// UTC-Instant → {date: "YYYY-MM-DD", minutes} in der Kalender-Zeitzone
function tzParts(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const g = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
        timeZone: CAL_TZ, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(d).map((p) => [p.type, p.value]));
    return {
        date: `${g.year}-${g.month}-${g.day}`,
        minutes: (parseInt(g.hour, 10) % 24) * 60 + parseInt(g.minute, 10),
    };
}
// Datums-Anteil eines Timestamps in der Kalender-Zeitzone
const tzDateStr = (iso) => (iso ? (tzParts(iso)?.date || "") : "");
// Wandzeit in der Kalender-Zeitzone → UTC-ISO (für Speichern/Drop)
function tzWallToIso(dayIsoDate, hours, minutes) {
    const pretendUtc = new Date(`${dayIsoDate}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00Z`);
    const g = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
        timeZone: CAL_TZ, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    }).formatToParts(pretendUtc).map((p) => [p.type, p.value]));
    const asUtc = Date.UTC(+g.year, +g.month - 1, +g.day, +g.hour % 24, +g.minute, +g.second);
    const offsetMin = (asUtc - pretendUtc.getTime()) / 60000;
    return new Date(pretendUtc.getTime() - offsetMin * 60000).toISOString();
}

// „Heute" in der Kalender-Zeitzone (Jul-2026, User-Wunsch: fest Berlin-Zeit,
// unabhängig von der Browser-Zeitzone)
const tzTodayStr = () => tzDateStr(new Date().toISOString());
const tzTodayDate = () => new Date(`${tzTodayStr()}T00:00:00`);

// Lokales Kalenderdatum (Spalten-Label) — NICHT via toISOString (UTC-Shift!)
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => {
    const c = new Date(d);
    c.setDate(c.getDate() + n);
    return c;
};
const startOfWeek = (d) => {
    const c = new Date(d);
    const day = c.getDay() || 7; // Montag = 1
    if (day !== 1) c.setDate(c.getDate() - (day - 1));
    c.setHours(0, 0, 0, 0);
    return c;
};
const fmtDay = (d) => d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
const fmtTime = (iso) => (iso && tzParts(iso))
    ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: CAL_TZ })
    : "—";
const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

// Convert datetime → Y-offset (px) in the day grid (in Kalender-Zeitzone)
function pxFromTime(iso, dayIsoDate, hourStart, hourEnd) {
    if (!iso) return null;
    const p = tzParts(iso);
    if (!p || p.date !== dayIsoDate) return null;
    const minutes = p.minutes - hourStart * 60;
    if (minutes < 0 || minutes >= (hourEnd - hourStart) * 60) return null;
    return (minutes / SLOT_MIN) * PX_PER_SLOT;
}

// y-Koordinate (px) zurück in Datetime (für Drop) — Wandzeit in Kalender-TZ
function timeFromPx(yPx, dayIsoDate, hourStart, hourEnd) {
    const slots = slotsCount(hourStart, hourEnd);
    const slot = Math.max(0, Math.min(slots - 1, Math.floor(yPx / PX_PER_SLOT)));
    const minutes = slot * SLOT_MIN + hourStart * 60;
    return tzWallToIso(dayIsoDate, Math.floor(minutes / 60), minutes % 60);
}

// ──────────────────────────────────────────────────────────────────────────
//  Main component
// ──────────────────────────────────────────────────────────────────────────
export default function WorkshopCalendarPage() {
    const { user } = useAuth();
    const gearsOk = useGearsVisible();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Jul-2026 (User-Wunsch): Kalender-Zeitzone (Standard Europe/Berlin, umschaltbar)
    const [calTz, setCalTzState] = useState(getCalTz());
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Jul-2026 (User-Wunsch): Ansicht/Datum aus URL-Parametern (für QR-Aufruf am Handy)
    const [view, setView] = useState(() => (searchParams.get("view") === "day" ? "day" : "week"));
    const [anchor, setAnchor] = useState(() => {
        const q = searchParams.get("date");
        return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? new Date(`${q}T00:00:00`) : tzTodayDate();
    });
    const [data, setData] = useState({ resources: [], scheduled: [], unscheduled: [], from: null, to: null });
    // Aug-2026 (User-Wunsch): „Gehe zu Tag" zeigt NUR den Tag (Tagesansicht);
    // Klick auf „Woche" löscht die Eingabe und zeigt die Woche des Tags.
    const [gotoDay, setGotoDay] = useState("");
    // Aug-2026 (User-Wunsch) — Universalsuche über Kunden-/Fahrzeugdaten
    const [calSearch, setCalSearch] = useState("");
    const [calResults, setCalResults] = useState(null);
    const [pendingFocusId, setPendingFocusId] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resourceModalOpen, setResourceModalOpen] = useState(false);
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    const [scheduleModal, setScheduleModal] = useState(null);  // for confirm/edit duration
    const [shareOrder, setShareOrder] = useState(null);        // Jul-2026: Teilen aus Kalender
    const [autoStartId, setAutoStartId] = useState(null);      // „Einplanen & Start" Deep-Link
    const [showSnoozed, setShowSnoozed] = useState(false);
    const [utilOpen, setUtilOpen] = useState(false); // Aug-2026: Auslastungs-Ansicht

    const days = useMemo(() => {
        if (view === "day") return [anchor];
        const start = startOfWeek(anchor);
        return Array.from({ length: 6 }, (_, i) => addDays(start, i)); // Mo–Sa (6 Tage)
    }, [view, anchor]);

    const dayRange = useMemo(() => ({
        from: isoDate(days[0]),
        to: isoDate(days[days.length - 1]),
    }), [days]);

    const gridHours = useMemo(
        () => deriveGridHours(data.opening_hours, data.opening_hours_enabled),
        [data.opening_hours, data.opening_hours_enabled],
    );

    // {employee_id: [absence,…]} für graue Tagesblöcke abwesender Mechaniker
    const absencesByEmployee = useMemo(() => {
        const map = {};
        (data.absences || []).forEach((a) => {
            if (!a.employee_id) return;
            (map[a.employee_id] = map[a.employee_id] || []).push(a);
        });
        return map;
    }, [data.absences]);

    // {employee_id: [punch,…]} Stempelzeiten für die Kalender-Anzeige (Jul-2026)
    const punchesByEmployee = useMemo(() => {
        const m = {};
        (data.punches || []).forEach((p) => {
            if (!p.employee_id) return;
            (m[p.employee_id] = m[p.employee_id] || []).push(p);
        });
        return m;
    }, [data.punches]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/workshop/calendar?from=${dayRange.from}&to=${dayRange.to}`);
            setData(res.data || { resources: [], scheduled: [], unscheduled: [] });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kalender konnte nicht geladen werden");
        }
        try {
            const br = await api.get(`/workshop/blocks?from_date=${dayRange.from}&to_date=${dayRange.to}`);
            setBlocks(br.data?.items || []);
        } catch (_e) {
            setBlocks([]);
        }
        setLoading(false);
    }, [dayRange.from, dayRange.to]);

    // Aug-2026 (User-Bug) — Online-Buchung mit einem Klick in Auftrag übernehmen,
    // damit der Termin im Kalender-Raster erscheint.
    const convertBooking = async (b) => {
        try {
            const r = await api.post(`/workshop/bookings/${b.id}/convert-to-order`);
            toast.success(`Auftrag ${r.data.work_order_number || ""} erstellt — Termin ist jetzt im Kalender`.replace("  ", " "));
            loadData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Übernahme fehlgeschlagen");
        }
    };

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");
        if (user) loadData();
    }, [user, navigate, loadData]);

    // Jun-2026 (User-Wunsch) — Deep-Link vom Auftrag in den Kalender:
    //  ?schedule=<id> → unterminierter Auftrag im Termin-Dialog öffnen
    //  ?focus=<id>    → terminierten Auftrag anzeigen (Woche springt + Dialog)
    const deepLinkRef = useRef(null);
    useEffect(() => {
        if (loading) return;
        const sId = searchParams.get("schedule");
        const fId = searchParams.get("focus");
        const key = sId || fId;
        if (!key || deepLinkRef.current === key) return;
        deepLinkRef.current = key;
        const clearParam = (name) => {
            const n = new URLSearchParams(searchParams);
            n.delete(name);
            setSearchParams(n, { replace: true });
        };
        if (sId) {
            const all = [...(data.unscheduled || []), ...(data.snoozed || []), ...(data.scheduled || [])];
            const o = all.find((x) => x.id === sId);
            if (o) {
                setScheduleModal(o);
                // „Einplanen & Start": Auftrag nach Termin-Speichern direkt starten
                if (searchParams.get("start") === "1") setAutoStartId(sId);
            } else toast.info("Auftrag nicht gefunden oder bereits terminiert.");
            const n = new URLSearchParams(searchParams);
            n.delete("schedule");
            n.delete("start");
            setSearchParams(n, { replace: true });
        } else if (fId) {
            // Bevorzugt das bereits „geshapte" Kalender-Objekt (enthält customer_label,
            // customer_email, work_titles …) — API-Rohauftrag nur als Fallback.
            const all = [...(data.unscheduled || []), ...(data.snoozed || []), ...(data.scheduled || [])];
            const shaped = all.find((x) => x.id === fId);
            if (shaped) {
                if (shaped.appointment_at) setAnchor(new Date(`${tzDateStr(shaped.appointment_at)}T00:00:00`));
                setScheduleModal(shaped);
            } else {
                api.get(`/work-orders/${fId}`).then(async (r) => {
                    const o = r.data;
                    if (!o) return;
                    if (o.appointment_at) setAnchor(new Date(`${tzDateStr(o.appointment_at)}T00:00:00`));
                    // Kundenname nachladen (Rohauftrag enthält nur customer_id)
                    if (o.customer_id) {
                        try {
                            const c = (await api.get(`/customers/${o.customer_id}`)).data || {};
                            o.customer_label = c.company_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                            o.customer_email = c.email;
                        } catch { /* best-effort */ }
                    }
                    setScheduleModal(o);
                }).catch(() => toast.error("Auftrag konnte nicht geladen werden"));
            }
            clearParam("focus");
        }
    }, [loading, data, searchParams, setSearchParams]);

    const schedule = async (orderId, body, opts = {}) => {
        try {
            const res = await api.patch(`/work-orders/${orderId}/schedule`, body);
            if (opts.silent) { loadData(); return res.data; }
            const n = res.data?.notification;
            if (n?.sent) toast.success(`Termin gespeichert · Bestätigung an ${n.to} gesendet`);
            else if (body.notify_customer && n && !n.sent) toast.warning(`Termin gespeichert — Bestätigung nicht versendet (${n.reason || "Fehler"})`);
            else toast.success("Termin gespeichert");
            if ((res.data?.conflicts || []).length > 0) {
                toast.warning(
                    `⚠️ Konflikt mit ${res.data.conflicts.length} anderen Auftrag/Aufträgen (${[...new Set(res.data.conflicts.map((c) => c.kind === "mechaniker" ? "Monteur" : "Ressource"))].join(" + ")})`,
                    { duration: 5000 },
                );
            }
            loadData();
        } catch (e) {
            if (opts.silent) throw e;
            toast.error(e.response?.data?.detail || "Termin konnte nicht gespeichert werden");
        }
        return undefined;
    };

    const unschedule = async (orderId) => {
        try {
            await api.delete(`/work-orders/${orderId}/schedule`);
            toast.success("Termin entfernt");
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        }
    };

    // Universalsuche: debounced Abfrage + Treffer anspringen
    useEffect(() => {
        if ((calSearch || "").trim().length < 2) { setCalResults(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const res = await api.get(`/workshop/calendar/search?q=${encodeURIComponent(calSearch.trim())}`);
                setCalResults(res.data?.results || []);
            } catch { setCalResults([]); }
        }, 350);
        return () => clearTimeout(t);
    }, [calSearch]);

    useEffect(() => {
        if (!pendingFocusId) return;
        const hit = [...(data.scheduled || []), ...(data.unscheduled || [])].find((o) => o.id === pendingFocusId);
        if (hit) { setScheduleModal(hit); setPendingFocusId(null); }
    }, [data, pendingFocusId]);

    const pickSearchHit = (hit) => {
        setCalResults(null);
        setCalSearch("");
        setPendingFocusId(hit.id);
        if (hit.appointment_at) {
            // Aug-2026 (User-Wunsch): direkt zur Tagesansicht des Termins springen
            setAnchor(new Date(hit.appointment_at));
            setView("day");
        }
    };

    const onDropToSlot = (orderId, dayIso, resourceId, yPx, currentDurationMin) => {
        const appointment_at = timeFromPx(yPx, dayIso, gridHours.start, gridHours.end);
        schedule(orderId, {
            appointment_at,
            duration_min: currentDurationMin || 60,
            resource_id: resourceId === "__none__" ? null : resourceId,
        });
    };

    // Aug-2026 (User-Wunsch) — Auftrag direkt drucken (aus Kalender & Termin-Dialog).
    // Tab wird SYNCHRON geöffnet (sonst Popup-Blocker); die PDF wird danach per
    // iframe in den Tab gerendert (Blob-Navigation ist in Chromium unzuverlässig).
    const printOrder = async (o) => {
        const w = window.open("", "_blank");
        try {
            const r = await api.get(`/work-orders/${o.id}/print-bundle.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            if (w && !w.closed) {
                w.document.title = `${o.order_number || "Auftrag"} — Druck`;
                w.document.body.style.margin = "0";
                const f = w.document.createElement("iframe");
                f.src = url;
                f.style.cssText = "border:0;width:100vw;height:100vh";
                w.document.body.appendChild(f);
            } else {
                window.open(url, "_blank");
            }
            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (e) {
            if (w) w.close();
            toast.error(e.response?.data?.detail || "Druck fehlgeschlagen");
        }
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="workshop-calendar-page">
            {/* Header */}
            <div className="bg-[#111827] text-white">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
                    <div className="flex items-end justify-between gap-4 mt-3 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight inline-flex items-center gap-2">
                                <CalendarDays className="w-7 h-7" /> Werkstatt-Kalender
                                <SettingsShortcut to="/dashboard?tab=settings#settings-werkstatt" title="Werkstatt-Einstellungen" testid="settings-shortcut-werkstatt-kalender" dark />
                            </h1>
                            <p className="text-sm text-gray-400 mt-1 truncate max-w-full">
                                Termine per Drag&amp;Drop zuweisen. Ressourcen verwalten (Hebebühnen, Mitarbeiter).
                            </p>
                        </div>
                        {/* Jun-2026 (User-Wunsch): QR ganz rechts, links davon die zwei Button-Zeilen */}
                        <div className="flex items-end justify-end gap-3 md:gap-4 ml-auto max-w-full">
                        <div className="flex-1 flex flex-col items-end gap-2 min-w-0">
                            {/* Zeile 1: Navigation (Routing zu anderen Bereichen) */}
                            <div className="flex gap-2 flex-wrap justify-end max-w-full" data-testid="cal-nav-row">
                                <Button
                                    onClick={() => navigate("/dashboard")}
                                    variant="outline"
                                    className="bg-transparent border-gray-500 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm text-xs font-bold uppercase tracking-wider"
                                    data-testid="back-to-dashboard"
                                >
                                    ← Dashboard
                                </Button>
                                <Button
                                    onClick={() => navigate("/dealer/workshop-orders")}
                                    className="bg-[#F97316] hover:bg-[#EA580C] text-[#111827] rounded-sm text-xs font-bold uppercase tracking-wider"
                                    data-testid="link-workshop-orders"
                                >
                                    ← Aufträge
                                </Button>
                            </div>
                            {/* Zeile 2: Aktionen dieser Seite */}
                            <div className="flex gap-2 flex-wrap justify-end max-w-full" data-testid="cal-action-row">
                                <select
                                    value={calTz}
                                    onChange={(e) => { setCalTz(e.target.value); setCalTzState(e.target.value); }}
                                    className="bg-transparent border border-gray-500 text-gray-300 text-xs rounded-sm px-2 h-10"
                                    title={`Kalender-Zeitzone (Browser erkannt: ${browserTz})`}
                                    data-testid="cal-tz-select"
                                >
                                    {[...new Set([...CAL_TZ_OPTIONS, browserTz])].map((tz) => (
                                        <option key={tz} value={tz} className="text-gray-900">
                                            {tz.replace("Europe/", "")}{tz === browserTz ? " (erkannt)" : ""}
                                        </option>
                                    ))}
                                </select>
                                {gearsOk && <Button
                                    onClick={() => setResourceModalOpen(true)}
                                    variant="outline"
                                    className="border-gray-500 text-white hover:bg-gray-700 hover:text-white rounded-sm"
                                    data-testid="manage-resources-btn"
                                >
                                    <Settings className="w-4 h-4 mr-1.5" /> Ressourcen
                                </Button>}
                                <Button
                                    onClick={() => setBlockModalOpen(true)}
                                    variant="outline"
                                    className="border-gray-500 text-white hover:bg-gray-700 hover:text-white rounded-sm"
                                    data-testid="add-block-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1.5" /> Blockzeit
                                </Button>
                            </div>
                        </div>
                        {/* QR: aktuelle Kalender-Ansicht schnell am Handy öffnen */}
                        <div className="shrink-0 flex flex-col items-center gap-1" data-testid="cal-header-qr">
                            <div className="bg-white rounded-md p-1.5 shadow-lg">
                                <QRCodeSVG
                                    value={`${window.location.origin}/dealer/workshop-calendar?view=${view}&date=${isoDate(anchor)}`}
                                    size={72} level="M" title="Kalender-Ansicht am Handy öffnen"
                                />
                            </div>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1">
                                <Smartphone className="w-3 h-3" /> Am Handy öffnen
                            </span>
                        </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Jul-2026 (User-Wunsch) — öffentlicher Buchungslink im Kalender,
                über der Datumsanzeige (aus dem Buchungen-Bereich hierher verschoben). */}
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-4">
                <PublicBookingLink />
            </div>

            {/* Toolbar */}
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setAnchor(addDays(anchor, view === "day" ? -1 : -7))} className="rounded-sm" data-testid="cal-prev" aria-label={view === "day" ? "Vorheriger Tag" : "Vorherige Woche"}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setAnchor(tzTodayDate()); setGotoDay(""); }} className="rounded-sm uppercase tracking-wider text-xs" data-testid="cal-today">
                        Heute
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setAnchor(addDays(anchor, view === "day" ? 1 : 7))} className="rounded-sm" data-testid="cal-next" aria-label={view === "day" ? "Nächster Tag" : "Nächste Woche"}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    {/* Aug-2026 (User-Wunsch) — Gehe zu Tag: zeigt NUR diesen Tag */}
                    <input
                        type="date"
                        value={gotoDay}
                        onChange={(e) => {
                            const v = e.target.value;
                            setGotoDay(v);
                            if (v) { setAnchor(new Date(`${v}T00:00:00`)); setView("day"); }
                        }}
                        className="h-9 border border-gray-300 rounded-sm px-2 text-xs font-mono-num bg-white hover:border-gray-400"
                        title="Gehe zu Tag (zeigt nur diesen Tag)"
                        data-testid="cal-goto-date"
                    />
                    {/* Aug-2026 (User-Wunsch) — Universalsuche Kunden/Fahrzeuge */}
                    <div className="relative">
                        <div className="flex items-center h-9 border border-gray-300 rounded-sm bg-white px-2 gap-1.5 focus-within:border-gray-500">
                            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <input
                                value={calSearch}
                                onChange={(e) => setCalSearch(e.target.value)}
                                placeholder="Kunde, Kennzeichen, FIN, Auftrag…"
                                className="text-xs outline-none w-52 bg-transparent"
                                data-testid="cal-search-input"
                            />
                            {calSearch && (
                                <button type="button" onClick={() => { setCalSearch(""); setCalResults(null); }} className="text-gray-400 hover:text-black" data-testid="cal-search-clear">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        {calResults !== null && (
                            <div className="absolute z-50 mt-1 w-[380px] max-h-80 overflow-auto bg-white border border-gray-300 rounded-sm shadow-xl" data-testid="cal-search-results">
                                {calResults.length === 0 ? (
                                    <p className="text-xs text-gray-500 p-3">Keine Treffer</p>
                                ) : calResults.map((h) => (
                                    <button key={h.id} type="button" onClick={() => pickSearchHit(h)}
                                        className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-0"
                                        data-testid={`cal-search-hit-${h.order_number}`}>
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="font-mono-num font-bold">{h.order_number}</span>
                                            <span className="truncate">{h.customer_label}</span>
                                            <span className="ml-auto shrink-0 font-mono-num text-[10px] text-gray-500">
                                                {h.appointment_at ? new Date(h.appointment_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "ohne Termin"}
                                            </span>
                                        </div>
                                        <div className="text-[10px] text-gray-500 truncate">
                                            {[h.vehicle_label, h.customer_zip ? `PLZ ${h.customer_zip}` : ""].filter(Boolean).join(" · ")}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="font-display font-black text-lg ml-2" data-testid="cal-range">
                        {view === "day"
                            ? anchor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                            : `KW ${getISOWeek(days[0])} · ${days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} – ${days[days.length - 1].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button
                        size="sm"
                        variant={view === "day" ? "default" : "outline"}
                        onClick={() => setView("day")}
                        className={`rounded-sm uppercase tracking-wider text-xs ${view === "day" ? "bg-[#111827] hover:bg-black text-white" : ""}`}
                        data-testid="cal-view-day"
                    >
                        Tag
                    </Button>
                    <Button
                        size="sm"
                        variant={view === "week" ? "default" : "outline"}
                        onClick={() => { setView("week"); setGotoDay(""); }}
                        className={`rounded-sm uppercase tracking-wider text-xs ${view === "week" ? "bg-[#111827] hover:bg-black text-white" : ""}`}
                        data-testid="cal-view-week"
                    >
                        Woche
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setUtilOpen(true)}
                        className="rounded-sm uppercase tracking-wider text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        title="Mechaniker-Auslastung der Woche für diesen Standort"
                        data-testid="cal-utilization-btn"
                    >
                        <Gauge className="w-3.5 h-3.5 mr-1" /> Auslastung
                    </Button>
                </div>
            </div>

            {/* Main layout: unscheduled side + calendar grid */}
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 grid grid-cols-12 gap-4">
                {/* Unscheduled sidebar */}
                <aside className="col-span-12 lg:col-span-3 order-2 lg:order-1">
                    <div className="bg-white border border-gray-200 rounded-sm">
                        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1.5">
                                <Inbox className="w-3.5 h-3.5" /> Ungeplant ({data.unscheduled.length})
                            </div>
                        </div>
                        <div className="p-2 space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="unscheduled-list" tabIndex={0} role="region" aria-label="Nicht geplante Aufträge">
                            {data.unscheduled.length === 0 ? (
                                <div className="text-center text-xs text-gray-500 py-6">Keine ungeplanten Aufträge.</div>
                            ) : data.unscheduled.map((o) => (
                                <UnscheduledCard key={o.id} order={o} resources={data.resources} onClick={() => setScheduleModal(o)} />
                            ))}
                        </div>
                        {/* Aug-2026 (User-Bug): Online-Buchungen waren im Kalender unsichtbar */}
                        {(data.bookings || []).length > 0 && (
                            <div className="border-t border-gray-200">
                                <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-blue-800 bg-blue-50 flex items-center gap-1.5">
                                    <Smartphone className="w-3.5 h-3.5" /> Online-Termine ({data.bookings.length})
                                </div>
                                <div className="p-2 space-y-1.5 max-h-[40vh] overflow-y-auto" data-testid="calendar-bookings-list">
                                    {data.bookings.map((b) => (
                                        <div key={b.id} className="border border-blue-200 bg-blue-50/50 rounded-sm p-2" data-testid={`cal-booking-${b.id}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-xs font-bold truncate">{b.customer_name}</span>
                                                <span className="font-mono-num text-[10px] font-bold text-blue-800 shrink-0">{fmtDateDe(b.date)} · {b.time_slot}</span>
                                            </div>
                                            <div className="text-[10px] text-gray-600 truncate">
                                                {[b.service_type, [b.vehicle_brand, b.vehicle_model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                                            </div>
                                            <Button size="sm" onClick={() => convertBooking(b)}
                                                className="mt-1.5 h-6 w-full text-[10px] rounded-sm bg-[#111827] hover:bg-black text-white font-bold"
                                                data-testid={`cal-booking-convert-${b.id}`}>
                                                → In Auftrag übernehmen
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(data.snoozed && data.snoozed.length > 0) && (
                            <div className="border-t border-gray-200">
                                <button type="button" onClick={() => setShowSnoozed((v) => !v)}
                                    className="w-full px-3 py-2 flex items-center justify-between text-xs font-bold text-gray-600 hover:bg-gray-50"
                                    data-testid="snoozed-toggle" aria-expanded={showSnoozed}>
                                    <span>💤 schlummert ({data.snoozed.length})</span>
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSnoozed ? "rotate-180" : ""}`} />
                                </button>
                                {showSnoozed && (
                                    <div className="p-2 space-y-2 max-h-[40vh] overflow-y-auto" data-testid="snoozed-list">
                                        {data.snoozed.map((o) => (
                                            <div key={o.id}>
                                                <UnscheduledCard order={o} resources={data.resources} onClick={() => setScheduleModal(o)} />
                                                <div className="text-[10px] text-amber-700 pl-1 mt-0.5">💤 schlummert bis {fmtDateDe(o.snoozed_until)}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </aside>

                {/* Calendar grid */}
                <section className="col-span-12 lg:col-span-9 order-1 lg:order-2">
                    {loading ? (
                        <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500">Lade Kalender…</div>
                    ) : data.resources.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="no-resources">
                            <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <div className="font-display font-black text-lg">Noch keine Ressourcen</div>
                            <p className="text-sm text-gray-500 mt-1 mb-4">Lege Hebebühnen, Mitarbeiter oder Stationen an, um den Kalender zu nutzen.</p>
                            <Button onClick={() => setResourceModalOpen(true)} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="empty-add-resource">
                                <Plus className="w-4 h-4 mr-1.5" /> Erste Ressource anlegen
                            </Button>
                        </div>
                    ) : (
                        <CalendarGrid
                            days={days}
                            resources={data.resources}
                            scheduled={data.scheduled}
                            blocks={blocks}
                            absencesByEmployee={absencesByEmployee}
                            punchesByEmployee={punchesByEmployee}
                            hourStart={gridHours.start}
                            hourEnd={gridHours.end}
                            onDropToSlot={onDropToSlot}
                            onCardClick={(o) => setScheduleModal(o)}
                            onPrintOrder={printOrder}
                            onResourceSettings={() => setResourceModalOpen(true)}
                        />
                    )}

                    {/* Blockzeiten-Liste (Phase 4c+++) */}
                    {blocks.length > 0 && (
                        <div className="mt-4 bg-white border border-gray-200 rounded-sm" data-testid="blocks-list">
                            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" /> Blockzeiten & Abwesenheiten ({blocks.length})
                                </div>
                            </div>
                            <div className="p-2 space-y-1.5 max-h-72 overflow-y-auto">
                                {blocks.map((b) => (
                                    <BlockListRow
                                        key={b.id}
                                        block={b}
                                        resources={data.resources}
                                        onDelete={async (series) => {
                                            try {
                                                await api.delete(`/workshop/blocks/${b.id}${series ? "?series=true" : ""}`);
                                                toast.success(series ? "Serie gelöscht" : "Block gelöscht");
                                                loadData();
                                            } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {/* Modals */}
            {utilOpen && <UtilizationModal onClose={() => setUtilOpen(false)} />}
            {resourceModalOpen && (
                <ResourceManagerModal
                    resources={data.resources}
                    mechanics={data.mechanics || []}
                    onClose={() => { setResourceModalOpen(false); loadData(); }}
                />
            )}
            {blockModalOpen && (
                <BlockManagerModal
                    resources={data.resources}
                    onClose={() => setBlockModalOpen(false)}
                    onSaved={() => { setBlockModalOpen(false); loadData(); }}
                />
            )}
            {scheduleModal && (
                <ScheduleDetailModal
                    order={scheduleModal}
                    resources={data.resources}
                    mechanics={data.mechanics || []}
                    autoStart={autoStartId === scheduleModal.id && scheduleModal.status === "confirmed"}
                    absencesByEmployee={absencesByEmployee}
                    openingHours={data.opening_hours}
                    openingEnabled={data.opening_hours_enabled}
                    onClose={() => { setScheduleModal(null); setAutoStartId(null); }}
                    onSave={async (body) => { await schedule(scheduleModal.id, body); setScheduleModal(null); setAutoStartId(null); }}
                    onAutoSave={async (body) => { await schedule(scheduleModal.id, body, { silent: true }); }}
                    onPrint={() => printOrder(scheduleModal)}
                    onRemove={async () => { await unschedule(scheduleModal.id); setScheduleModal(null); }}
                    onShare={() => { setShareOrder(scheduleModal); setScheduleModal(null); setAutoStartId(null); }}
                    onSnooze={async (days) => {
                        try {
                            await api.post(`/work-orders/${scheduleModal.id}/snooze`, { days });
                            toast.success(days > 0 ? `Auftrag schlummert (${days === 7 ? "1 Woche" : days + " Tag(e)"})` : "Schlummern aufgehoben");
                            setScheduleModal(null);
                            loadData();
                        } catch { toast.error("Schlummern fehlgeschlagen"); }
                    }}
                />
            )}
            {/* Jul-2026 (User-Wunsch) — Teilen direkt aus dem Kalender-Popup */}
            {shareOrder && (
                <ShareOrderModal
                    order={shareOrder}
                    onClose={() => setShareOrder(null)}
                    onShared={() => { setShareOrder(null); loadData(); }}
                />
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
//  Calendar Grid
// ──────────────────────────────────────────────────────────────────────────
function CalendarGrid({ days, resources, scheduled, blocks, absencesByEmployee, punchesByEmployee, hourStart, hourEnd, onDropToSlot, onCardClick, onPrintOrder, onResourceSettings }) {
    const dragData = useRef(null); // {orderId, durationMin}
    const slotsPerDay = slotsCount(hourStart, hourEnd);

    const handleDragStart = (e, order) => {
        dragData.current = { orderId: order.id, durationMin: order.duration_min || 60 };
        e.dataTransfer.setData("text/plain", order.id);
        e.dataTransfer.effectAllowed = "move";
    };
    const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
    const handleDrop = (e, dayIso, resourceId) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const yPx = e.clientY - rect.top;
        const orderId = e.dataTransfer.getData("text/plain") || dragData.current?.orderId;
        if (!orderId) return;
        onDropToSlot(orderId, dayIso, resourceId, yPx, dragData.current?.durationMin || 60);
        dragData.current = null;
    };

    // Time labels column on the left
    const hourLabels = [];
    for (let h = hourStart; h <= hourEnd; h++) {
        hourLabels.push({ h, top: (h - hourStart) * (60 / SLOT_MIN) * PX_PER_SLOT });
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="calendar-grid" tabIndex={0} role="region" aria-label="Werkstatt-Kalenderraster">
            <div className="inline-flex min-w-full">
                {/* Hour labels column */}
                <div className="shrink-0 border-r border-gray-200 bg-gray-50">
                    <div className="h-[68px] border-b border-gray-200" />
                    <div className="h-4 border-b border-gray-200" />{/* Ausgleich: Stempelzeiten-Strip */}
                    <div className="relative" style={{ height: slotsPerDay * PX_PER_SLOT, width: 56 }}>
                        {hourLabels.map((hl) => (
                            <div
                                key={hl.h}
                                className="absolute left-0 right-0 text-[10px] font-mono-num text-gray-500 pr-1 text-right -mt-1.5"
                                style={{ top: hl.top }}
                            >
                                {String(hl.h).padStart(2, "0")}:00
                            </div>
                        ))}
                    </div>
                </div>

                {/* Days */}
                {days.map((day) => (
                    <DayColumn
                        key={day.toISOString()}
                        day={day}
                        resources={resources}
                        scheduled={scheduled}
                        blocks={blocks || []}
                        absencesByEmployee={absencesByEmployee || {}}
                        punchesByEmployee={punchesByEmployee || {}}
                        hourStart={hourStart}
                        hourEnd={hourEnd}
                        onDragStart={handleDragStart}
                        allowDrop={allowDrop}
                        onDrop={handleDrop}
                        onCardClick={onCardClick}
                        onPrintOrder={onPrintOrder}
                        onResourceSettings={onResourceSettings}
                    />
                ))}
            </div>
        </div>
    );
}

function DayColumn({ day, resources, scheduled, blocks, absencesByEmployee, punchesByEmployee, hourStart, hourEnd, onDragStart, allowDrop, onDrop, onCardClick, onPrintOrder, onResourceSettings }) {
    const dayIso = isoDate(day);
    const isToday = tzTodayStr() === dayIso;
    // Aug-2026 (User-Wunsch): heutige Spalte beim Aufruf mittig scrollen
    const colRef = useRef(null);
    useEffect(() => {
        if (isToday && colRef.current) {
            const t = setTimeout(() => {
                try { colRef.current.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); } catch { /* noop */ }
            }, 250);
            return () => clearTimeout(t);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isToday]);

    // Auslastungs-Heatmap: scheduled min vs. Kapazität (resources × Öffnungszeit)
    const dayOrders = scheduled.filter((o) => tzDateStr(o.appointment_at) === dayIso);
    const bookedMin = dayOrders.reduce((s, o) => s + (Number(o.duration_min) || 60), 0);
    const capacityMin = Math.max(1, resources.length * (hourEnd - hourStart) * 60);
    const loadPct = Math.min(1, bookedMin / capacityMin);
    const loadColor =
        loadPct >= 0.9 ? "bg-red-500" :
        loadPct >= 0.7 ? "bg-orange-400" :
        loadPct >= 0.3 ? "bg-amber-300" :
        loadPct > 0 ? "bg-emerald-400" : "bg-gray-200";
    const loadLabel = bookedMin > 0
        ? `${(bookedMin / 60).toFixed(1).replace(".", ",")} h / ${Math.round(capacityMin / 60)} h · ${Math.round(loadPct * 100)} %`
        : "frei";

    // Blocks für diesen Tag (über alle Stunden)
    const dayBlocks = (blocks || []).filter((b) => {
        const blockStart = tzDateStr(b.starts_at);
        const blockEnd = tzDateStr(b.ends_at);
        return blockStart <= dayIso && blockEnd >= dayIso;
    });
    // Tagesweite Blocks (ohne Ressourcen-Bindung)
    const allDayBlocks = dayBlocks.filter((b) => !b.resource_id);

    return (
        <div ref={colRef} className="flex-1 min-w-[260px] border-r border-gray-200 last:border-r-0" data-testid={`day-col-${dayIso}`}>
            {/* Day header + Auslastungs-Heatmap */}
            <div className={`h-[68px] border-b border-gray-200 px-2 py-1 ${isToday ? "bg-[#FFCC00]/15" : "bg-gray-50"}`}>
                <div className="flex items-baseline justify-between gap-1">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                        {day.toLocaleDateString("de-DE", { weekday: "short" })}
                    </div>
                    <div className="text-[9px] font-mono-num text-gray-500 truncate" title={loadLabel} data-testid={`day-load-label-${dayIso}`}>
                        {loadLabel}
                    </div>
                </div>
                <div className={`font-display font-black text-lg ${isToday ? "text-[#111827]" : "text-gray-700"}`}>
                    {day.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                </div>
                <div className="h-1 mt-0.5 rounded-sm bg-gray-200 overflow-hidden" title={loadLabel} data-testid={`day-load-bar-${dayIso}`}>
                    <div
                        className={`h-full ${loadColor} transition-all`}
                        style={{ width: `${Math.round(loadPct * 100)}%` }}
                    />
                </div>
            </div>

            {/* All-day-Blocks Badge im Tag-Header (max 2 angezeigt) */}
            {allDayBlocks.length > 0 && (
                <div className="px-2 pt-1 pb-2 bg-gray-50 border-b border-gray-200 space-y-0.5" data-testid={`all-day-blocks-${dayIso}`}>
                    {allDayBlocks.slice(0, 2).map((b) => (
                        <div
                            key={b.id}
                            className="text-[9px] font-bold px-1 py-0.5 rounded-sm bg-amber-100 border border-amber-300 text-amber-900 truncate"
                            title={`${b.type_label}${b.custom_label ? " · " + b.custom_label : ""}`}
                        >
                            ⛔ {b.type_label}
                            {b.custom_label ? ` · ${b.custom_label}` : ""}
                        </div>
                    ))}
                    {allDayBlocks.length > 2 && (
                        <div className="text-[9px] text-gray-500 italic">+ {allDayBlocks.length - 2} weitere</div>
                    )}
                </div>
            )}

            {/* Resource sub-columns (+ „Ohne Ressource" — Aug-2026: Termine ohne
                Ressourcen-Zuweisung müssen IMMER sichtbar sein) */}
            <div className="flex">
                {(() => {
                    const unassigned = dayOrders.filter(
                        (o) => !o.resource_id || !resources.some((r) => r.id === o.resource_id));
                    const lanes = unassigned.length > 0
                        ? [...resources, { id: "__none__", name: "Ohne Ressource", short_label: "Ohne Ressource", type: "none" }]
                        : resources;
                    return lanes.map((r) => {
                    const orders = r.id === "__none__" ? unassigned : scheduled.filter(
                        (o) => o.resource_id === r.id && tzDateStr(o.appointment_at) === dayIso,
                    );
                    const resBlocks = dayBlocks.filter((b) => b.resource_id === r.id || (!b.resource_id && b.employee_id));
                    // Abwesenheit des verknüpften Mechanikers an diesem Tag?
                    const mechAbsences = r.mechanic_id ? (absencesByEmployee[r.mechanic_id] || []) : [];
                    const absence = mechAbsences.find(
                        (a) => (a.date_from || "") <= dayIso && (a.date_to || "") >= dayIso,
                    ) || null;
                    // Punches des verknüpften Mechanikers an diesem Tag (Jul-2026)
                    const mechPunchesAll = r.mechanic_id ? (punchesByEmployee[r.mechanic_id] || []) : [];
                    const punches = mechPunchesAll.filter(
                        (p) => tzDateStr(p.clock_in) === dayIso,
                    );
                    return (
                        <ResourceSlot
                            key={r.id}
                            day={dayIso}
                            resource={r}
                            orders={orders}
                            blocks={resBlocks}
                            absence={absence}
                            punches={punches}
                            hourStart={hourStart}
                            hourEnd={hourEnd}
                            onDragStart={onDragStart}
                            allowDrop={allowDrop}
                            onDrop={onDrop}
                            onCardClick={onCardClick}
                            onPrintOrder={onPrintOrder}
                            onResourceSettings={onResourceSettings}
                        />
                    );
                    });
                })()}
            </div>
        </div>
    );
}

// Jul-2026 (User-Wunsch): dynamische Zeit-Hinweise für den Mechaniker —
// vergleicht Ist-Zeit (jetzt/Stempelbeginn) mit dem geplanten Termin-Fenster.
function orderTimeHint(o, dayIso, punches) {
    if (o.status !== "in_progress" || !o.appointment_at) return null;
    const now = new Date();
    if (tzDateStr(now.toISOString()) !== dayIso) return null;
    const start = new Date(o.appointment_at);
    const end = new Date(start.getTime() + (o.duration_min || 60) * 60000);
    if (now < start) {
        const firstIn = (punches || [])[0]?.clock_in;
        if (firstIn && new Date(firstIn) <= now) {
            return { text: "▶ Du hast früher begonnen", cls: "text-blue-800",
                title: "Der Auftrag läuft, obwohl der geplante Termin noch nicht erreicht ist — guter Vorsprung!" };
        }
        return null;
    }
    if (now <= end) {
        const pct = Math.round(((now - start) / (end - start)) * 100);
        return { text: `✓ Gut in der Zeit (${pct} %)`, cls: "text-emerald-800",
            title: `${pct} % der geplanten Zeit verbraucht — du liegst gut in der Zeit.` };
    }
    const overMin = Math.round((now - end) / 60000);
    return { text: `⚠ ${overMin} Min drüber — zurückstellen?`, cls: "text-red-700",
        title: `Geplante Zeit um ${overMin} Minuten überschritten. Auftrag ggf. zurückstellen oder Termin verlängern.` };
}

function ResourceSlot({ day, resource, orders, blocks, absence, punches, hourStart, hourEnd, onDragStart, allowDrop, onDrop, onCardClick, onPrintOrder, onResourceSettings }) {
    const slotsPerDay = slotsCount(hourStart, hourEnd);
    const totalH = slotsPerDay * PX_PER_SLOT;
    const halfHourLines = [];
    for (let i = 0; i <= slotsPerDay; i++) {
        halfHourLines.push({
            top: i * PX_PER_SLOT,
            major: i % 2 === 0,
        });
    }
    // Block-Cards berechnen (Slot-Position via pxFromTime)
    const dayBlocks = (blocks || []).filter((b) => {
        const sd = tzDateStr(b.starts_at);
        const ed = tzDateStr(b.ends_at);
        return sd <= day && ed >= day;
    });
    const bookedMin = (orders || []).reduce((s, o) => s + (o.duration_min || 60), 0);
    // Stempelzeiten-Anzeige (Jul-2026): Kommen/Gehen des verknüpften Mechanikers
    const punchOpen = (punches || []).some((p) => !p.clock_out);
    const punchLabel = (punches || []).length
        ? `⏱ ${punches.map((p) => `${fmtTime(p.clock_in)}–${p.clock_out ? fmtTime(p.clock_out) : "…"}`).join(" · ")}`
        : (resource.mechanic_id && !absence ? "⏱ —" : "");
    const firstIn = (punches || [])[0]?.clock_in;
    const earlyStart = firstIn && (tzParts(firstIn)?.minutes ?? Infinity) < hourStart * 60;
    const punchTitle = (punches || []).length
        ? `Stempelzeiten ${resource.mechanic_name || ""}: ${punches.map((p) => `${fmtTime(p.clock_in)}–${p.clock_out ? fmtTime(p.clock_out) : "offen"}`).join(", ")}${earlyStart ? " · Früher begonnen (vor Kalenderbeginn)" : ""}${punchOpen ? " · aktuell eingestempelt" : ""}`
        : (resource.mechanic_id ? "Keine Stempelzeiten an diesem Tag" : "");
    return (
        <div className="flex-1 min-w-[120px] border-r border-gray-100 last:border-r-0">
            {/* Resource label + Hover-Zusatzdaten */}
            <div className="group relative px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-center bg-gray-100 hover:bg-gray-200 border-b border-gray-200 truncate cursor-pointer"
                onClick={() => resource.id !== "__none__" && onResourceSettings?.()}
                title="Einstellungen dieser Ressource öffnen"
                data-testid={`res-col-${resource.id}`}>
                <span className="mr-0.5">{RESOURCE_TYPE_ICON[resource.type] || "•"}</span>
                {resource.short_label || resource.name}
                <div className="hidden group-hover:block absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 w-48 bg-[#111827] text-white text-left rounded-sm p-2 shadow-lg text-[10px] normal-case tracking-normal font-normal" data-testid={`res-tip-${resource.id}`}>
                    <div className="font-bold text-xs mb-0.5">{resource.name}</div>
                    <div className="opacity-80">{RESOURCE_TYPE_LABEL[resource.type] || resource.type}</div>
                    {resource.short_label && <div className="opacity-80">Kürzel: {resource.short_label}</div>}
                    {resource.mechanic_name && <div className="opacity-90 mt-1">👤 {resource.mechanic_name}</div>}
                    {Array.isArray(resource.specialties) && resource.specialties.length > 0 && (
                        <div className="opacity-80 mt-0.5">🔧 Arbeitsbereich: {resource.specialties.join(", ")}</div>
                    )}
                    <div className="mt-1">Heute belegt: <b>{(orders || []).length}</b> Auftrag(e){bookedMin ? ` · ${(bookedMin / 60).toFixed(1)} h` : ""}</div>
                    {resource.notes && <div className="opacity-80 mt-1">{resource.notes}</div>}
                    <div className="mt-1">{resource.active === false ? "⛔ inaktiv" : "✔ aktiv"}</div>
                    <div className="mt-1.5 pt-1.5 border-t border-white/20 text-[#FFCC00] font-bold">⚙ Klicken für Einstellungen</div>
                </div>
            </div>
            {/* Stempelzeiten-Strip (Kommen/Gehen) — feste Höhe für Spalten-Ausrichtung */}
            <div
                className={`h-4 px-1 text-[8px] font-mono-num text-center truncate border-b border-gray-200 leading-4 ${punchOpen ? "bg-emerald-50 text-emerald-800 font-bold" : (punches || []).length ? "bg-gray-50 text-gray-600" : "bg-gray-50 text-gray-300"}`}
                title={punchTitle}
                data-testid={`punch-strip-${resource.id}-${day}`}
            >
                {punchLabel}
            </div>
            {/* Grid + cards */}
            <div
                className="relative bg-white"
                style={{ height: totalH }}
                onDragOver={allowDrop}
                onDrop={(e) => onDrop(e, day, resource.id)}
                data-testid={`drop-${day}-${resource.id}`}
            >
                {/* Abwesenheits-Overlay (Urlaub/Krank/…) — Spalte ausgrauen */}
                {absence && (
                    <div
                        className="absolute inset-0 z-20 bg-gray-300/70 flex items-center justify-center text-center px-1"
                        style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 8px, rgba(0,0,0,0.06) 8px 16px)" }}
                        title={`${resource.mechanic_name || "Mechaniker"} – ${absence.label} bis ${fmtDateDe(absence.date_to)}`}
                        data-testid={`absence-overlay-${resource.id}-${day}`}
                    >
                        <div className="text-[10px] font-bold text-gray-700 leading-tight rotate-0">
                            <div className="uppercase tracking-wider">{absence.label}</div>
                            <div className="opacity-80">bis {fmtDateDe(absence.date_to)}</div>
                        </div>
                    </div>
                )}
                {halfHourLines.map((line, idx) => (
                    <div
                        key={idx}
                        className={`absolute left-0 right-0 ${line.major ? "border-t border-gray-200" : "border-t border-dashed border-gray-100"}`}
                        style={{ top: line.top }}
                    />
                ))}
                {/* Block-Cards (im Hintergrund, gestreift) */}
                {dayBlocks.map((b) => {
                    const top = pxFromTime(b.starts_at, day, hourStart, hourEnd);
                    if (top === null) return null;
                    // Block-Dauer in Minuten (gleicher Tag): ends_at - starts_at, sonst bis Tagesende
                    const startD = new Date(b.starts_at);
                    const endD = new Date(b.ends_at);
                    const sameDay = tzDateStr(b.starts_at) === tzDateStr(b.ends_at);
                    const durMin = sameDay
                        ? Math.max(SLOT_MIN, (endD - startD) / 60000)
                        : (hourEnd - hourStart) * 60;
                    const height = Math.max(PX_PER_SLOT, (durMin / SLOT_MIN) * PX_PER_SLOT - 1);
                    const tone = {
                        tuev_pruefer: "bg-blue-100/85 border-blue-500 text-blue-900",
                        werkstatt_reinigung: "bg-indigo-100/85 border-indigo-500 text-indigo-900",
                        hof_reinigung: "bg-purple-100/85 border-purple-500 text-purple-900",
                        urlaub_schule_ausgleich: "bg-amber-100/85 border-amber-500 text-amber-900",
                        anderer_einsatzort: "bg-orange-100/85 border-orange-500 text-orange-900",
                        sonstiges: "bg-gray-100/85 border-gray-500 text-gray-900",
                    }[b.type] || "bg-gray-100/85 border-gray-500 text-gray-900";
                    return (
                        <div
                            key={`block-${b.id}`}
                            className={`absolute left-0.5 right-0.5 px-1 py-0.5 rounded-sm border-l-4 text-[9px] leading-tight ${tone} overflow-hidden`}
                            style={{
                                top,
                                height,
                                backgroundImage: "repeating-linear-gradient(45deg, transparent 0 6px, rgba(0,0,0,0.05) 6px 12px)",
                            }}
                            title={`${b.type_label}${b.custom_label ? " · " + b.custom_label : ""} (${fmtTime(b.starts_at)}–${fmtTime(b.ends_at)})`}
                            data-testid={`block-card-${b.id}`}
                        >
                            <div className="font-bold truncate">⛔ {b.type_label}</div>
                            {b.custom_label && <div className="truncate italic opacity-90">{b.custom_label}</div>}
                        </div>
                    );
                })}
                {orders.map((o) => {
                    const top = pxFromTime(o.appointment_at, day, hourStart, hourEnd);
                    if (top === null) return null;
                    const height = Math.max(PX_PER_SLOT, ((o.duration_min || 60) / SLOT_MIN) * PX_PER_SLOT - 2);
                    const colorClass = STATUS_COLOR[o.status] || STATUS_COLOR.confirmed;
                    // Jul-2026: dynamischer Zeit-Hinweis für in_progress-Aufträge
                    const hint = orderTimeHint(o, day, punches);
                    return (
                        <div
                            key={o.id}
                            draggable
                            onDragStart={(e) => onDragStart(e, o)}
                            onClick={() => onCardClick(o)}
                            className={`group absolute left-0.5 right-0.5 pl-2.5 pr-1 py-0.5 rounded-sm cursor-move overflow-hidden text-[10px] leading-tight ${colorClass} hover:ring-2 hover:ring-[#111827] z-10`}
                            style={{ top, height }}
                            title={`${o.order_number} · ${o.customer_label} · ${o.vehicle_label}`}
                            data-testid={`event-${o.id}`}
                        >
                            <CategoryStripe order={o} />
                            {onPrintOrder && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onPrintOrder(o); }}
                                    className="absolute top-0.5 right-0.5 p-0.5 rounded-sm bg-white/85 text-gray-700 opacity-0 group-hover:opacity-100 hover:bg-white hover:text-black transition-opacity z-20"
                                    title="Auftrag drucken"
                                    data-testid={`event-print-${o.id}`}
                                >
                                    <Printer className="w-3 h-3" />
                                </button>
                            )}
                            <div className="font-bold font-mono-num truncate">{fmtTime(o.appointment_at)} · {o.order_number}</div>
                            <div className="truncate font-bold">{o.customer_label}</div>
                            <div className="truncate text-[9px] opacity-80">{o.vehicle_label}</div>
                            {o.mechanic_name && <div className="truncate text-[9px] opacity-90 inline-flex items-center gap-0.5"><Wrench className="w-2 h-2" /> {o.mechanic_name}</div>}
                            {o.estimated_completion && (() => { const a = completionAmpel(o.estimated_completion); return (
                                <div className="truncate text-[9px] inline-flex items-center gap-1" title={`Wunschtermin: ${fmtDateDe(o.estimated_completion)}${a ? " · " + a.t : ""}`}><span className={`w-1.5 h-1.5 rounded-full ${a ? a.c : "bg-gray-400"}`} />{fmtDateDe(o.estimated_completion)}</div>
                            ); })()}
                            {hint && (
                                <div className={`truncate text-[9px] font-bold ${hint.cls}`} title={hint.title} data-testid={`order-hint-${o.id}`}>{hint.text}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
//  Unscheduled card (draggable source)
// ──────────────────────────────────────────────────────────────────────────
function UnscheduledCard({ order, onClick, resources }) {
    const onDragStart = (e) => {
        e.dataTransfer.setData("text/plain", order.id);
        // Set hint values via dataTransfer item channel (we re-read via ref in grid; fallback only)
        e.dataTransfer.effectAllowed = "move";
    };
    const colorClass = STATUS_COLOR[order.status] || STATUS_COLOR.confirmed;
    const resourceName = order.resource_id
        ? ((resources || []).find((r) => r.id === order.resource_id)?.name || null)
        : null;
    return (
        <div
            draggable
            onDragStart={onDragStart}
            onClick={onClick}
            title="Klicken für Vorschau & Termin"
            className={`relative pl-3 border-l-0 rounded-sm p-2 text-[11px] cursor-pointer hover:ring-2 hover:ring-[#111827] ${colorClass}`}
            data-testid={`unscheduled-${order.id}`}
        >
            <CategoryStripe order={order} />
            <div className="flex items-center justify-between gap-1">
                <div className="font-mono-num font-bold text-[12px]">{order.order_number}</div>
                <Badge className="bg-white/60 text-[8px] uppercase tracking-wider font-bold rounded-sm border border-current">
                    {STATUS_LABEL_DE[order.status] || order.status}
                </Badge>
            </div>
            <div className="font-bold mt-0.5 truncate" title={order.customer_label}>{order.customer_label}</div>
            <div className="text-[10px] opacity-80 inline-flex items-center gap-1 truncate" title={order.vehicle_label}>
                <Car className="w-2.5 h-2.5" /> {order.vehicle_label}{order.vehicle_plate ? ` · ${order.vehicle_plate}` : ""}
            </div>
            {order.customer_complaint && (
                <div className="text-[9px] mt-0.5 italic line-clamp-2 opacity-75">„{order.customer_complaint}&quot;</div>
            )}
            {/* Aug-2026 (User-Wunsch): Erstelldatum + Wunschtermin anzeigen */}
            <div className="text-[9px] mt-1 flex flex-wrap gap-x-2 gap-y-0.5 opacity-90" data-testid={`unscheduled-meta-${order.id}`}>
                {order.created_at && (
                    <span className="inline-flex items-center gap-0.5">
                        <Plus className="w-2.5 h-2.5" /> Erstellt {fmtDateDe(order.created_at.slice(0, 10))}
                    </span>
                )}
                {order.estimated_completion && (
                    <span className="inline-flex items-center gap-0.5 font-bold text-blue-900">
                        <CalendarDays className="w-2.5 h-2.5" /> Wunsch {fmtDateDe(order.estimated_completion.slice(0, 10))}
                    </span>
                )}
            </div>
            {/* Aug-2026 (User-Wunsch): vorgeplanten Monteur + Ressource anzeigen */}
            {(order.employee || resourceName) && (
                <div className="text-[9px] mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5" data-testid={`unscheduled-assign-${order.id}`}>
                    {order.employee && (
                        <span className="inline-flex items-center gap-0.5 font-bold">
                            <Wrench className="w-2.5 h-2.5" /> {order.employee}
                        </span>
                    )}
                    {resourceName && (
                        <span className="inline-flex items-center gap-0.5 opacity-80">
                            <MapPin className="w-2.5 h-2.5" /> {resourceName}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
//  Resource Manager Modal
// ──────────────────────────────────────────────────────────────────────────
function ResourceManagerModal({ resources, mechanics, onClose }) {
    const [items, setItems] = useState(resources || []);
    const [draft, setDraft] = useState({ name: "", type: "lift", short_label: "", sort_order: 0, mechanic_id: "", specialties: [] });
    const [busy, setBusy] = useState(false);
    const mechList = mechanics || [];

    const reload = async () => {
        const r = await api.get("/workshop/resources?include_inactive=true");
        setItems(r.data || []);
    };

    const add = async () => {
        if (!draft.name.trim()) { toast.error("Name erforderlich"); return; }
        setBusy(true);
        try {
            await api.post("/workshop/resources", {
                name: draft.name,
                type: draft.type,
                short_label: draft.short_label || null,
                sort_order: Number(draft.sort_order) || 0,
                mechanic_id: draft.type === "lift" ? (draft.mechanic_id || null) : null,
                specialties: draft.type === "lift" ? (draft.specialties || []) : [],
            });
            toast.success("Ressource angelegt");
            setDraft({ name: "", type: "lift", short_label: "", sort_order: 0, mechanic_id: "", specialties: [] });
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    const toggleDraftSpec = (s) => {
        setDraft((d) => ({
            ...d,
            specialties: d.specialties.includes(s) ? d.specialties.filter((x) => x !== s) : [...d.specialties, s],
        }));
    };

    const update = async (id, patch) => {
        try {
            await api.patch(`/workshop/resources/${id}`, patch);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Ressource löschen?")) return;
        try {
            await api.delete(`/workshop/resources/${id}`);
            toast.success("Ressource entfernt");
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="resource-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h2 className="font-display font-black text-lg">Ressourcen verwalten</h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="resource-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Aug-2026 (User-Wunsch): Mechaniker ohne feste Hebebühne sichtbar machen —
                        die Zuordnung steuert die Ressourcen-Automatik im Werkstattauftrag. */}
                    {(() => {
                        const assigned = new Set(items.filter((r) => r.type === "lift" && r.active !== false && r.mechanic_id).map((r) => r.mechanic_id));
                        const unassigned = mechList.filter((m) => !assigned.has(m.id));
                        if (mechList.length === 0 || unassigned.length === 0) return null;
                        return (
                            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-sm p-2.5 text-[11px] text-amber-900" data-testid="resource-unassigned-mechanics">
                                <b>Ohne feste Hebebühne:</b> {unassigned.map((m) => m.name).join(", ")} — bitte bei der jeweiligen
                                Hebebühne zuordnen, damit im Werkstattauftrag bei der Mechaniker-Zuweisung automatisch die richtige
                                Hebebühne gesetzt wird (ohne Zuordnung wird nur nach dem Namen geraten).
                            </div>
                        );
                    })()}
                    {/* Add new */}
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 inline-flex items-center gap-1">Neue Ressource <InfoHint id="workshop_cal.new_resource" defaultText="Ressourcen sind die buchbaren Kapazitäten der Werkstatt: Hebebühnen, Mitarbeiter:innen oder Stationen. Aufträge werden ihnen im Kalender zugewiesen — so siehst du die Auslastung und vermeidest Doppelbelegungen." side="right" /></div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <Input
                                placeholder="Name (z. B. Hebebühne 1)"
                                value={draft.name}
                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                className="md:col-span-2 rounded-sm"
                                data-testid="new-resource-name"
                            />
                            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                                <SelectTrigger className="rounded-sm" data-testid="new-resource-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.entries(RESOURCE_TYPE_LABEL).map(([k, l]) => (
                                        <SelectItem key={k} value={k}>{RESOURCE_TYPE_ICON[k]} {l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                placeholder="Kurz (H1)"
                                maxLength={8}
                                value={draft.short_label}
                                onChange={(e) => setDraft({ ...draft, short_label: e.target.value })}
                                className="rounded-sm"
                            />
                            <Input
                                type="number"
                                placeholder="Sort"
                                value={draft.sort_order}
                                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                                className="rounded-sm"
                            />
                        </div>
                        {draft.type === "lift" && (
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="new-resource-lift-extra">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Fester Mitarbeiter (Mechaniker/Kundendienst)</label>
                                    <Select value={draft.mechanic_id || "none"} onValueChange={(v) => setDraft({ ...draft, mechanic_id: v === "none" ? "" : v })}>
                                        <SelectTrigger className="rounded-sm h-9 text-sm mt-0.5" data-testid="new-resource-mechanic"><SelectValue placeholder="— kein —" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— kein —</SelectItem>
                                            {mechList.map((m) => (
                                                <SelectItem key={m.id} value={m.id}>{m.name}{m.employee_function === "kundendienst" ? " · Kundendienst" : ""}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                        {mechList.length === 0
                                            ? "Keine Mechaniker am Standort hinterlegt."
                                            : "Wird im Werkstattauftrag bei Mechaniker-Zuweisung automatisch mit ausgewählt."}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Spezialisierungen</label>
                                    <div className="flex flex-wrap gap-1 mt-1" data-testid="new-resource-specialties">
                                        {SPECIALTIES.map((s) => {
                                            const on = draft.specialties.includes(s);
                                            return (
                                                <button key={s} type="button" onClick={() => toggleDraftSpec(s)}
                                                    className={`text-[10px] px-2 py-1 rounded-sm border font-bold ${on ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                                                    data-testid={`new-spec-${s}`}>
                                                    {s}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                        <Button
                            onClick={add}
                            disabled={busy}
                            className="mt-2 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                            data-testid="new-resource-submit"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Anlegen
                        </Button>
                    </div>

                    {/* List */}
                    <div className="space-y-1.5" data-testid="resource-list">
                        {items.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 py-4">Noch keine Ressourcen.</div>
                        ) : items.map((r) => (
                            <ResourceRow key={r.id} resource={r} mechanics={mechList} onUpdate={update} onRemove={remove} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ResourceRow({ resource, mechanics, onUpdate, onRemove }) {
    const [editing, setEditing] = useState(false);
    const mechList = mechanics || [];
    const [form, setForm] = useState({
        name: resource.name,
        type: resource.type,
        short_label: resource.short_label || "",
        sort_order: resource.sort_order || 0,
        active: resource.active,
        mechanic_id: resource.mechanic_id || "",
        specialties: Array.isArray(resource.specialties) ? resource.specialties : [],
    });
    const toggleSpec = (s) => {
        setForm((f) => ({
            ...f,
            specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s],
        }));
    };
    const save = () => {
        onUpdate(resource.id, {
            name: form.name,
            type: form.type,
            sort_order: Number(form.sort_order) || 0,
            short_label: form.short_label || null,
            active: form.active,
            mechanic_id: form.type === "lift" ? (form.mechanic_id || null) : null,
            specialties: form.type === "lift" ? form.specialties : [],
        });
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="border border-blue-300 bg-blue-50 rounded-sm p-2 space-y-2" data-testid={`res-edit-${resource.id}`}>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="md:col-span-2 rounded-sm h-8 text-sm" />
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(RESOURCE_TYPE_LABEL).map(([k, l]) => (
                                <SelectItem key={k} value={k}>{l}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input value={form.short_label} onChange={(e) => setForm({ ...form, short_label: e.target.value })} className="rounded-sm h-8 text-sm" />
                    <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="rounded-sm h-8 text-sm" />
                    <div className="flex gap-1">
                        <Button size="sm" onClick={save} className="rounded-sm h-8 text-xs bg-[#111827] hover:bg-black text-white" data-testid={`res-save-${resource.id}`}>OK</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="rounded-sm h-8 text-xs">X</Button>
                    </div>
                </div>
                {form.type === "lift" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Fester Mitarbeiter (Mechaniker/Kundendienst)</label>
                            <Select value={form.mechanic_id || "none"} onValueChange={(v) => setForm({ ...form, mechanic_id: v === "none" ? "" : v })}>
                                <SelectTrigger className="rounded-sm h-8 text-sm mt-0.5" data-testid={`res-mechanic-${resource.id}`}><SelectValue placeholder="— kein —" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— kein —</SelectItem>
                                    {mechList.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>{m.name}{m.employee_function === "kundendienst" ? " · Kundendienst" : ""}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-[10px] text-gray-500 mt-0.5">Wird im Werkstattauftrag bei Mechaniker-Zuweisung automatisch mit ausgewählt.</div>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Spezialisierungen</label>
                            <div className="flex flex-wrap gap-1 mt-1" data-testid={`res-specialties-${resource.id}`}>
                                {SPECIALTIES.map((s) => {
                                    const on = form.specialties.includes(s);
                                    return (
                                        <button key={s} type="button" onClick={() => toggleSpec(s)}
                                            className={`text-[10px] px-2 py-1 rounded-sm border font-bold ${on ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className={`border rounded-sm p-2 flex items-center justify-between gap-2 ${resource.active ? "bg-white border-gray-200" : "bg-gray-100 border-gray-300 opacity-60"}`} data-testid={`res-row-${resource.id}`}>
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <Badge className="bg-gray-200 text-gray-800 text-[10px] uppercase tracking-wider font-bold rounded-sm shrink-0">
                    {RESOURCE_TYPE_ICON[resource.type] || "•"} {RESOURCE_TYPE_LABEL[resource.type] || resource.type}
                </Badge>
                <div className="font-bold truncate">{resource.name}</div>
                {resource.short_label && <span className="font-mono-num text-[10px] bg-gray-100 px-1 rounded-sm">{resource.short_label}</span>}
                {resource.mechanic_name && <span className="text-[10px] text-gray-600">👤 {resource.mechanic_name}</span>}
                {resource.type === "lift" && resource.active && !resource.mechanic_id && (
                    <span className="text-[10px] font-bold text-amber-700" data-testid={`res-no-mechanic-${resource.id}`}>⚠ kein Mechaniker zugeordnet</span>
                )}
                {Array.isArray(resource.specialties) && resource.specialties.length > 0 && (
                    <span className="text-[10px] text-gray-500 truncate">🔧 {resource.specialties.join(", ")}</span>
                )}
                {!resource.active && <Badge className="bg-red-100 text-red-700 text-[9px] rounded-sm uppercase">Inaktiv</Badge>}
            </div>
            <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-sm h-7 text-xs" data-testid={`res-edit-btn-${resource.id}`}>
                    <Edit2 className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => onRemove(resource.id)} className="rounded-sm h-7 text-xs text-red-700 hover:bg-red-50" data-testid={`res-del-${resource.id}`}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────
//  Schedule Detail Modal (click on event)
// ──────────────────────────────────────────────────────────────────────────
function ScheduleDetailModal({ order, resources, mechanics, autoStart, absencesByEmployee, openingHours, openingEnabled, onClose, onSave, onAutoSave, onPrint, onRemove, onSnooze, onShare }) {
    const [duration, setDuration] = useState(order.duration_min || 60);
    const [resourceId, setResourceId] = useState(order.resource_id || "");
    const [employeeId, setEmployeeId] = useState(order.employee_id || "");
    const [notifyCustomer, setNotifyCustomer] = useState(!!order.customer_email);
    const [appointmentAt, setAppointmentAt] = useState(() => {
        if (!order.appointment_at) return "";
        // UTC → Wandzeit in der Kalender-Zeitzone (datetime-local-Wert)
        const p = tzParts(order.appointment_at);
        if (!p) return "";
        return `${p.date}T${String(Math.floor(p.minutes / 60)).padStart(2, "0")}:${String(p.minutes % 60).padStart(2, "0")}`;
    });

    const save = () => {
        if (!appointmentAt) { toast.error("Termin-Datum/-Uhrzeit fehlt"); return; }
        if (selectedAbsence) { toast.error(`Mechaniker abwesend (${selectedAbsence.label} bis ${fmtDateDe(selectedAbsence.date_to)}) — bitte andere Hebebühne oder anderen Termin wählen`); return; }
        const [dPart, tPart] = appointmentAt.split("T");
        const wallMins = parseInt(tPart.slice(0, 2), 10) * 60 + parseInt(tPart.slice(3, 5), 10);
        // Jun-2026 (User-Wunsch) — nur Tage/Uhrzeiten innerhalb der Öffnungszeiten zulassen.
        if (openingEnabled && Array.isArray(openingHours) && openingHours.length === 7) {
            const idx = (new Date(`${dPart}T00:00:00`).getDay() + 6) % 7; // 0=Mo … 6=So
            const row = openingHours[idx] || {};
            if (row.closed) { toast.error("An diesem Wochentag ist die Werkstatt geschlossen (Öffnungszeiten)."); return; }
            if (row.open && row.close) {
                const toMin = (s) => parseInt(s.slice(0, 2), 10) * 60 + parseInt(s.slice(3, 5), 10);
                if (wallMins < toMin(row.open) || wallMins >= toMin(row.close)) {
                    toast.error(`Termin liegt außerhalb der Öffnungszeiten (${row.open}–${row.close}).`); return;
                }
            }
        }
        onSave({
            // Wandzeit in Kalender-Zeitzone → UTC (Jul-2026 Zeitzonenfix)
            appointment_at: tzWallToIso(dPart, Math.floor(wallMins / 60), wallMins % 60),
            duration_min: Number(duration) || 60,
            resource_id: resourceId || null,
            // "" = Zuweisung entfernen (Backend-Sentinel), sonst Mechaniker-ID
            employee_id: employeeId || "",
            start_order: !!autoStart,
            notify_customer: !!(notifyCustomer && order.customer_email),
        });
    };

    // Aug-2026 (User-Wunsch) — Auto-Speichern im Hintergrund (ohne Dialog zu
    // schließen, ohne Kunden-Mail). Validierung still — bei Verstoß kein Save.
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    const autoMounted = useRef(false);
    useEffect(() => {
        if (!autoMounted.current) { autoMounted.current = true; return undefined; }
        if (!onAutoSave || !appointmentAt || selectedAbsence) return undefined;
        const t = setTimeout(async () => {
            const [dPart, tPart] = appointmentAt.split("T");
            const wallMins = parseInt(tPart.slice(0, 2), 10) * 60 + parseInt(tPart.slice(3, 5), 10);
            if (openingEnabled && Array.isArray(openingHours) && openingHours.length === 7) {
                const idx = (new Date(`${dPart}T00:00:00`).getDay() + 6) % 7;
                const row = openingHours[idx] || {};
                const toMin = (s) => parseInt(s.slice(0, 2), 10) * 60 + parseInt(s.slice(3, 5), 10);
                if (row.closed) return;
                if (row.open && row.close && (wallMins < toMin(row.open) || wallMins >= toMin(row.close))) return;
            }
            try {
                await onAutoSave({
                    appointment_at: tzWallToIso(dPart, Math.floor(wallMins / 60), wallMins % 60),
                    duration_min: Number(duration) || 60,
                    resource_id: resourceId || null,
                    employee_id: employeeId || "",
                    start_order: false,
                    notify_customer: false,
                });
                setAutoSavedAt(new Date());
            } catch { /* still — expliziter Speichern-Button zeigt Fehler */ }
        }, 1200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentAt, duration, resourceId, employeeId]);

    // Aug-2026 (User-Wunsch) — Mechaniker wählen → verknüpfte Ressource
    // automatisch mitsetzen (bleibt nachträglich änderbar).
    const pickMechanic = (v) => {
        const id = v === "none" ? "" : v;
        setEmployeeId(id);
        if (id) {
            const linked = (resources || []).find((r) => r.mechanic_id === id);
            if (linked) setResourceId(linked.id);
        }
    };

    // Aug-2026 (User-Bug, 3× reklamiert): Auch bei BEREITS vorher zugewiesenem
    // Mechaniker die verknüpfte Hebebühne automatisch vorbelegen — ohne Ressource
    // erscheint der Termin nicht im Kalender.
    useEffect(() => {
        if (employeeId && !resourceId) {
            const linked = (resources || []).find((r) => r.mechanic_id === employeeId);
            if (linked) setResourceId(linked.id);
        }
    }, [employeeId, resourceId, resources]);

    // Empfehlung: Hebebühnen, deren verknüpfter Mechaniker die passende
    // Spezialisierung hat, nach oben sortieren (andere bleiben auswählbar).
    const isMatch = (r) => Boolean(order.suggested_specialty && r.mechanic_specialty && r.mechanic_specialty === order.suggested_specialty);
    const sortedResources = [...(resources || [])].sort((a, b) => (isMatch(b) ? 1 : 0) - (isMatch(a) ? 1 : 0));

    // Abwesenheit: Hebebühnen sperren, deren verknüpfter Mechaniker am gewählten
    // Termintag abwesend ist (Urlaub/Krank etc.).
    const selDay = (appointmentAt || "").slice(0, 10);
    const absenceFor = (r) => {
        if (!selDay || !r.mechanic_id) return null;
        const list = (absencesByEmployee || {})[r.mechanic_id] || [];
        return list.find((a) => (a.date_from || "") <= selDay && (a.date_to || "") >= selDay) || null;
    };
    const selectedAbsence = resourceId ? absenceFor((resources || []).find((r) => r.id === resourceId) || {}) : null;

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="schedule-modal">
            <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="font-display font-black text-lg">Termin · {order.order_number}</h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="schedule-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 space-y-3">
                    {autoStart && (
                        <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-xs text-amber-900 font-bold" data-testid="schedule-autostart-hint">
                            ⚡ Einplanen &amp; Start: Beim Speichern wird der Auftrag direkt auf &bdquo;In Arbeit&quot; gesetzt.
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-5 text-sm">
                        <div>
                            <div className="text-gray-500 text-[10px] uppercase tracking-wider font-bold">Kunde</div>
                            <div className="font-bold" data-testid="schedule-customer">{order.customer_label || "—"}</div>
                        </div>
                        <div>
                            <div className="text-gray-500 text-[10px] uppercase tracking-wider font-bold">Fahrzeug</div>
                            <div>{order.vehicle_label}{order.vehicle_plate ? ` · ${order.vehicle_plate}` : ""}</div>
                        </div>
                    </div>
                    {/* Jul-2026 (User-Wunsch) — Kundenauftrag IMMER anzeigen */}
                    <div data-testid="schedule-complaint">
                        <div className="text-gray-500 text-[10px] uppercase tracking-wider font-bold">Kundenauftrag</div>
                        {order.customer_complaint
                            ? <div className="text-xs italic border-l-2 border-gray-300 pl-2 text-gray-600 mt-0.5">„{order.customer_complaint}&quot;</div>
                            : <div className="text-xs text-gray-400 mt-0.5">— nicht erfasst —</div>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
                    <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1">Termin <InfoHint id="workshop_cal.appointment" defaultText="Datum und Uhrzeit, zu der der Auftrag im Kalender eingeplant wird. Der Kunde erhält (falls hinterlegt) eine Terminbestätigung und 24 h vorher eine Erinnerung." side="bottom" /></label>
                        {/* Jul-2026 (User-Wunsch) — einheitlicher Kalender (Datum + Uhrzeit)
                            in allen Browsern identisch, statt nativem datetime-local. */}
                        <DateTimeField
                            value={appointmentAt}
                            onChange={setAppointmentAt}
                            className="mt-1"
                            data-testid="schedule-datetime"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Dauer (Minuten) <InfoHint id="workshop_cal.duration" defaultText="Geplante Arbeitsdauer. Bestimmt, wie lange die gewählte Ressource (Hebebühne/Mitarbeiter) im Kalender blockiert ist und fließt in die Tages-Auslastung ein." side="bottom" /></label>
                        <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                            <SelectTrigger className="rounded-sm mt-1" data-testid="schedule-duration"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {/* Aug-2026 (User-Wunsch): ab 120 min in 30-Minuten-Schritten */}
                                {[15, 30, 45, 60, 90, ...Array.from({ length: 13 }, (_, i) => 120 + i * 30)].map((m) => (
                                    <SelectItem key={m} value={String(m)}>{m} min ({(m / 60).toFixed(m % 60 ? 2 : 0).replace(".", ",")} h)</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700 inline-flex items-center gap-1">Ressource <InfoHint id="workshop_cal.resource" defaultText="Hebebühne, Mitarbeiter:in oder Station, der/die diesen Auftrag bearbeitet. Die Zuweisung verhindert Doppelbelegungen und zeigt die Auslastung je Ressource im Kalender." side="bottom" /></label>
                        {order.suggested_specialty && SPEC_LABEL[order.suggested_specialty] && (
                            <div className="text-[11px] text-emerald-700 mt-0.5 inline-flex items-center gap-1" data-testid="schedule-suggested-spec">
                                <Wrench className="w-3 h-3" /> Empfohlene Spezialisierung: <b>{SPEC_LABEL[order.suggested_specialty]}</b>
                            </div>
                        )}
                        <Select value={resourceId || "none"} onValueChange={(v) => setResourceId(v === "none" ? "" : v)}>
                            <SelectTrigger className="rounded-sm mt-1" data-testid="schedule-resource"><SelectValue placeholder="Ressource wählen…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— keine Zuweisung —</SelectItem>
                                {sortedResources.map((r) => {
                                    const match = isMatch(r);
                                    const abs = absenceFor(r);
                                    return (
                                        <SelectItem key={r.id} value={r.id} disabled={!!abs} data-testid={`schedule-resource-opt-${r.id}`}>
                                            {RESOURCE_TYPE_ICON[r.type] || "•"} {r.name}
                                            {r.mechanic_name ? ` · ${r.mechanic_name}` : ""}
                                            {Array.isArray(r.specialties) && r.specialties.length > 0 ? `  [${r.specialties.join(", ")}]` : ""}
                                            {abs ? `  — ${abs.label} bis ${fmtDateDe(abs.date_to)}` : (match ? "  ✓ passend" : "")}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        {selectedAbsence && (
                            <div className="mt-1.5 text-[11px] text-red-600 font-bold inline-flex items-center gap-1" data-testid="schedule-resource-absence-warn">
                                <Ban className="w-3 h-3" /> Mechaniker ist an diesem Tag abwesend ({selectedAbsence.label} bis {fmtDateDe(selectedAbsence.date_to)}). Bitte anderen Termin oder andere Hebebühne wählen.
                            </div>
                        )}
                    </div>
                    </div>{/* Ende Spalte links */}

                    <div className="space-y-3">
                    {/* Jun-2026 (User-Wunsch) — Mechaniker direkt im Termin-Dialog zuweisen */}
                    <div className={!employeeId ? "rounded-sm p-2 -m-2 ring-2 ring-amber-300 bg-amber-50/60" : ""}>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mechaniker (verantwortlich)</label>
                        <Select value={employeeId || "none"} onValueChange={pickMechanic}>
                            <SelectTrigger className="rounded-sm mt-1" data-testid="schedule-mechanic"><SelectValue placeholder="Mechaniker wählen…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— kein Mechaniker —</SelectItem>
                                {(mechanics || []).map((m) => (
                                    <SelectItem key={m.id} value={m.id} data-testid={`schedule-mechanic-opt-${m.id}`}>
                                        👤 {m.name}{m.mechanic_specialty && SPEC_LABEL[m.mechanic_specialty] ? ` · ${SPEC_LABEL[m.mechanic_specialty]}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!employeeId && (mechanics || []).length > 0 && (
                            <div className="mt-1 text-[11px] text-amber-800 font-bold" data-testid="schedule-mechanic-hint">Noch kein Mechaniker zugewiesen — bitte auswählen.</div>
                        )}
                    </div>

                    {(order.work_titles && order.work_titles.length > 0) && (
                        <div className="border border-gray-200 rounded-sm p-2" data-testid="schedule-works">
                            <div className="text-gray-500 text-[10px] uppercase tracking-wider font-bold mb-1">Beauftragte Arbeiten</div>
                            <ul className="text-xs space-y-0.5 list-disc pl-4">
                                {order.work_titles.map((w, i) => <li key={i} className="truncate">{w}</li>)}
                            </ul>
                        </div>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-2 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-bold" data-testid="schedule-status">{STATUS_LABEL_DE[order.status] || order.status}</span></div>
                        {order.mechanic_name && <div className="flex justify-between"><span className="text-gray-500">Monteur</span><span className="font-bold">{order.mechanic_name}</span></div>}
                        {order.estimated_completion && (() => { const a = completionAmpel(order.estimated_completion); return (
                            <div className="flex justify-between items-center"><span className="text-gray-500">Wunschtermin</span><span className="font-bold inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${a ? a.c : "bg-gray-400"}`} />{fmtDateDe(order.estimated_completion)}{a ? ` · ${a.t}` : ""}</span></div>
                        ); })()}
                        <div className="flex justify-between"><span className="text-gray-500">Positionen</span><span className="font-bold">{order.positions_count}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Brutto</span><span className="font-bold font-mono-num">{fmtEur(order.total_gross)}</span></div>
                    </div>
                    </div>{/* Ende Spalte rechts */}
                    </div>{/* Ende 2-Spalten-Grid */}

                    {!order.appointment_at && (
                        <div className="border border-gray-200 rounded-sm p-2" data-testid="schedule-snooze">
                            <div className="text-gray-500 text-[10px] uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Schlummern (aus &bdquo;Ungeplant&quot; ausblenden)</div>
                            {order.snoozed_until && <div className="text-[11px] text-amber-700 mb-1">schlummert bis {fmtDateDe(order.snoozed_until)}</div>}
                            <div className="flex gap-1.5 flex-wrap">
                                {[{ d: 1, l: "1 Tag" }, { d: 3, l: "3 Tage" }, { d: 7, l: "1 Woche" }].map((s) => (
                                    <Button key={s.d} size="sm" variant="outline" onClick={() => onSnooze(s.d)} className="rounded-sm h-7 text-xs" data-testid={`snooze-${s.d}`}>{s.l}</Button>
                                ))}
                                {order.snoozed_until && <Button size="sm" variant="outline" onClick={() => onSnooze(0)} className="rounded-sm h-7 text-xs text-emerald-700" data-testid="snooze-clear">Aufheben</Button>}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        {order.customer_email ? (
                            <label className="w-full flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-sky-50 border border-sky-200 rounded-sm px-2.5 py-2 -mt-1" data-testid="schedule-notify-toggle">
                                <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#111827]" data-testid="schedule-notify-cb" />
                                <span><span className="font-bold">Termin-Bestätigung an den Kunden senden</span><br /><span className="text-[11px] text-gray-500">E-Mail mit Datum &amp; Uhrzeit an {order.customer_email}</span></span>
                            </label>
                        ) : (
                            <div className="w-full text-[11px] text-gray-400 -mt-1" data-testid="schedule-notify-unavailable">Keine Kunden-E-Mail hinterlegt — Termin-Bestätigung nicht möglich.</div>
                        )}
                    </div>
                    <div className="flex gap-2 pt-1 items-center flex-wrap">
                        {order.appointment_at && (
                            <Button variant="outline" onClick={onRemove} className="rounded-sm text-red-700 hover:bg-red-50" data-testid="schedule-remove">
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Termin entfernen
                            </Button>
                        )}
                        {onPrint && (
                            <Button variant="outline" onClick={onPrint} className="rounded-sm" title="Auftrag drucken (PDF)" data-testid="schedule-print">
                                <Printer className="w-3.5 h-3.5 mr-1" /> Drucken
                            </Button>
                        )}
                        {onShare && (
                            <Button variant="outline" onClick={onShare} className="rounded-sm text-sky-700 border-sky-300 hover:bg-sky-50" title="Auftrag an eine andere Filiale / einen anderen Händler weitergeben" data-testid="schedule-share">
                                <Share2 className="w-3.5 h-3.5 mr-1" /> Teilen
                            </Button>
                        )}
                        <Button onClick={save} className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider rounded-sm" data-testid="schedule-save">
                            {autoStart ? "Speichern & starten" : "Speichern"}
                        </Button>
                    </div>
                    {autoSavedAt && (
                        <div className="text-[11px] text-emerald-700 font-bold text-right" data-testid="schedule-autosaved">
                            ✓ Automatisch gespeichert {autoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Utility: ISO week number
function getISOWeek(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}



// ──────────────────────────────────────────────────────────────────────────
//  Phase 4c+++ — Blockzeit-Verwaltung
// ──────────────────────────────────────────────────────────────────────────
const BLOCK_TYPES = [
    { value: "tuev_pruefer", label: "TÜV Prüfer im Hause" },
    { value: "werkstatt_reinigung", label: "Werkstatt-Reinigung" },
    { value: "hof_reinigung", label: "Hof-Reinigung" },
    { value: "anderer_einsatzort", label: "Heute tätig in …" },
    { value: "sonstiges", label: "Sonstiges" },
];

function BlockListRow({ block, resources, onDelete }) {
    const start = new Date(block.starts_at);
    const end = new Date(block.ends_at);
    const sameDay = tzDateStr(block.starts_at) === tzDateStr(block.ends_at);
    const resource = resources.find((r) => r.id === block.resource_id);
    const tone = {
        tuev_pruefer: "bg-blue-50 border-blue-400 text-blue-900",
        werkstatt_reinigung: "bg-indigo-50 border-indigo-400 text-indigo-900",
        hof_reinigung: "bg-purple-50 border-purple-400 text-purple-900",
        urlaub_schule_ausgleich: "bg-amber-50 border-amber-400 text-amber-900",
        anderer_einsatzort: "bg-orange-50 border-orange-400 text-orange-900",
        sonstiges: "bg-gray-50 border-gray-400 text-gray-900",
    }[block.type] || "bg-gray-50 border-gray-400 text-gray-900";
    return (
        <div className={`border-l-4 rounded-sm px-3 py-2 text-xs flex items-center justify-between gap-3 ${tone}`} data-testid={`block-row-${block.id}`}>
            <div className="flex-1 min-w-0">
                <div className="font-bold truncate">
                    {block.type_label}
                    {block.custom_label && <span className="font-normal text-gray-700"> · {block.custom_label}</span>}
                    {block.series_id && <span className="text-[9px] uppercase tracking-wider text-gray-500 ml-1">(Serie)</span>}
                </div>
                <div className="font-mono-num text-[11px] mt-0.5 text-gray-700">
                    {start.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: getCalTz() })}
                    {sameDay
                        ? ` – ${end.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: getCalTz() })}`
                        : ` – ${end.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: getCalTz() })}`}
                    {resource && <> · 🛠 {resource.name}</>}
                </div>
                {block.note && <div className="text-[11px] italic text-gray-600 mt-0.5">„{block.note}&quot;</div>}
            </div>
            <div className="flex gap-1 shrink-0">
                {block.series_id && (
                    <button
                        onClick={() => { if (window.confirm("Komplette Serie löschen?")) onDelete(true); }}
                        className="p-1.5 hover:bg-red-50 rounded-sm text-red-700"
                        title="Serie löschen"
                        data-testid={`block-del-series-${block.id}`}
                    >
                        <Trash2 className="w-3 h-3" />
                    </button>
                )}
                <button
                    onClick={() => { if (window.confirm("Diesen Block löschen?")) onDelete(false); }}
                    className="p-1.5 hover:bg-red-50 rounded-sm text-red-700"
                    title="Diesen Block löschen"
                    data-testid={`block-del-${block.id}`}
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}

function BlockManagerModal({ resources, onClose, onSaved }) {
    const [type, setType] = useState("tuev_pruefer");
    const [customLabel, setCustomLabel] = useState("");
    const today = tzTodayStr();
    const [startDate, setStartDate] = useState(today);
    const [startTime, setStartTime] = useState("08:00");
    const [endDate, setEndDate] = useState(today);
    const [endTime, setEndTime] = useState("17:00");
    const [employeeId, setEmployeeId] = useState("");
    const [resourceId, setResourceId] = useState("");
    const [note, setNote] = useState("");
    const [recurrence, setRecurrence] = useState("none");
    const [recurrenceUntil, setRecurrenceUntil] = useState("");
    const [employees, setEmployees] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/dealer/employees");
                setEmployees((r.data?.items || []).filter((e) => e.is_active !== false));
            } catch (_e) { /* ignore */ }
        })();
    }, []);

    const needsCustom = type === "sonstiges" || type === "anderer_einsatzort";

    const submit = async (e) => {
        e.preventDefault();
        if (needsCustom && !customLabel.trim()) {
            toast.error("Bitte Beschreibung angeben");
            return;
        }
        if (recurrence !== "none" && !recurrenceUntil) {
            toast.error("Bei Wiederkehr bitte „Bis\"-Datum setzen");
            return;
        }
        // Wandzeit in der Kalender-Zeitzone (Berlin) → UTC speichern (Jul-2026)
        const starts_at = tzWallToIso(startDate, +startTime.slice(0, 2), +startTime.slice(3, 5));
        const ends_at = tzWallToIso(endDate, +endTime.slice(0, 2), +endTime.slice(3, 5));
        if (new Date(ends_at) <= new Date(starts_at)) {
            toast.error("Ende muss nach dem Start liegen");
            return;
        }
        setBusy(true);
        try {
            const res = await api.post("/workshop/blocks", {
                type,
                custom_label: customLabel.trim() || null,
                starts_at,
                ends_at,
                employee_id: employeeId || null,
                resource_id: resourceId || null,
                note: note.trim() || null,
                recurrence,
                recurrence_until: recurrence === "none" ? null : recurrenceUntil,
            });
            toast.success(`${res.data.count} Block${res.data.count > 1 ? "s" : ""} angelegt`);
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Anlegen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="block-modal">
            <div className="bg-white rounded-sm shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                    <div className="font-display font-black text-lg">Blockzeit / Abwesenheit anlegen</div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm" data-testid="block-modal-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Typ *</label>
                        <select value={type} onChange={(e) => setType(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="block-type">
                            {BLOCK_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    {needsCustom && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Beschreibung *</label>
                            <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="z.B. Schulung Mannheim · Außendienst Filiale 2" maxLength={120} className="rounded-sm mt-1" data-testid="block-custom-label" />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Start *</label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required className="rounded-sm mt-1" data-testid="block-start-date" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Uhrzeit</label>
                            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required className="rounded-sm mt-1" data-testid="block-start-time" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ende *</label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="rounded-sm mt-1" data-testid="block-end-date" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Uhrzeit</label>
                            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required className="rounded-sm mt-1" data-testid="block-end-time" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mitarbeiter (optional)</label>
                            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="block-employee">
                                <option value="">— keiner —</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ressource (optional)</label>
                            <select value={resourceId} onChange={(e) => setResourceId(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="block-resource">
                                <option value="">— keine —</option>
                                {resources.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Notiz</label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className="rounded-sm mt-1" data-testid="block-note" />
                    </div>
                    {/* Recurrence */}
                    <div className="rounded-sm border border-gray-200 p-3 bg-gray-50">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Wiederholen</label>
                        <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="block-recurrence">
                            <option value="none">— Einzeltermin —</option>
                            <option value="daily">Täglich</option>
                            <option value="weekly">Wöchentlich</option>
                            <option value="monthly">Monatlich</option>
                        </select>
                        {recurrence !== "none" && (
                            <div className="mt-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Bis</label>
                                <Input type="date" value={recurrenceUntil} onChange={(e) => setRecurrenceUntil(e.target.value)} className="rounded-sm mt-1" data-testid="block-recurrence-until" />
                                <p className="text-[10px] text-gray-500 mt-1">Max. 365 Tage Vorlauf · einzelne Vorkommen können später gelöscht werden.</p>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" onClick={onClose} variant="outline" className="rounded-sm">Abbrechen</Button>
                        <Button type="submit" disabled={busy} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="block-submit">
                            {busy ? "Speichere…" : "Block anlegen"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
